/* eslint-disable tailwindcss/no-arbitrary-value */
import classNames from 'classnames';
import { CmsProduct } from './../types/cmsProduct';
import { useCmsClientContext } from './../utils/cmsPageContext';
import { isGreatEasternProduct } from './../utils/greatEasternDeeplink/isGreatEasternProduct';
import { getClientTranslations } from './../utils/translations/getClientTranslations';
import { tr } from './../utils/translations/tr';
import { ApplyCtaLink } from './ApplyCta';
import Button from './Button';
import { MaterialIcon } from './MaterialIcon';
import { TRANSLATIONS } from './ProductCard/i18n';

export type TravelInsuranceCtaType = 'productCard' | 'rewardAsideModal';

export type TravelInsuranceCtaBuilderProps = {
  product: CmsProduct;
  ctaLinkOverride?: string;
  e2eToogleButton?: boolean;
  e2eLabel?: string;
  ctaLabelOverride?: string;
  travelInsuranceCtaType: TravelInsuranceCtaType;
  sendAnalyticsForButtonApply: () => void;
};

export const TravelInsuranceCtaBuilder: React.FC<
  TravelInsuranceCtaBuilderProps
> = (props) => {
  const {
    product,
    ctaLinkOverride,
    e2eToogleButton,
    e2eLabel,
    ctaLabelOverride,
    travelInsuranceCtaType,
    sendAnalyticsForButtonApply,
  } = props;
  const { brand, lang, greatEasternDeeplink } = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, { brand, lang });

  const ctaClasses = classNames({
    'mhg-alpha-btn-small w-full md:w-general/2-col-width md:h-[--button-size-regular]':
      travelInsuranceCtaType === 'productCard',
    'mhg-alpha-btn-small w-23 md:mhg-alpha-btn md:w-40 lg:w-general/2-col-width':
      travelInsuranceCtaType === 'rewardAsideModal',
  });

  // Disable CTA when this is a GE product but the GE deeplink config is
  // missing from context — we cannot build a valid redirect URL, so let the
  // user see the disabled state rather than a broken link (CMS-1591). The
  // config comes from `constants/config.ts` via `CmsClientProvider`.
  const isGeDisabled = isGreatEasternProduct(product) && !greatEasternDeeplink;

  // CTA label is driven by product type (CMS-1771). Both branches let a manual
  // `ctaLabelOverride` win before falling back to the standardized default.
  const resolveCtaLabel = () => {
    // E2E — on-platform checkout: CMS `e2e_label` takes precedence.
    if (e2eToogleButton) {
      return e2eLabel || ctaLabelOverride || tr('express_checkout', staticText);
    }
    // Click-out — redirect to the insurer's official site.
    return ctaLabelOverride || tr('ti_click_out', staticText);
  };

  const buttonContent = (
    <div className="truncate px-2 flex items-center">
      {resolveCtaLabel()}
      <MaterialIcon
        icon={e2eToogleButton ? 'arrow_right_alt' : 'open_in_new'}
        size={2}
        className="ml-1 flex items-center justify-center size-[18px] md:size-6"
      />
    </div>
  );

  if (isGeDisabled) {
    // Wrap in a <span> with the same layout classes the <a> uses so width and
    // alignment do not shift when the CTA is disabled. No onClick — disabled
    // CTAs must not emit tracking.
    return (
      <div>
        <span className="block w-full md:inline md:w-auto">
          <Button styleType="primary" className={ctaClasses} disabled>
            {buttonContent}
          </Button>
        </span>
      </div>
    );
  }

  return (
    <div>
      <ApplyCtaLink
        product={product}
        ctaLinkOverride={ctaLinkOverride}
        e2eCTA={e2eToogleButton}
        className="block w-full md:inline md:w-auto"
        target="_blank"
        rel="noopener noreferrer"
      >
        <Button
          styleType={e2eToogleButton ? 'cta' : 'primary'}
          className={ctaClasses}
          onClick={sendAnalyticsForButtonApply}
        >
          {buttonContent}
        </Button>
      </ApplyCtaLink>
    </div>
  );
};
